/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export type NoteColor =
  | 'bg-amber-50 dark:bg-amber-950/20 text-amber-900 dark:text-amber-100 border-amber-200 dark:border-amber-900/30'
  | 'bg-rose-50 dark:bg-rose-950/20 text-rose-900 dark:text-rose-100 border-rose-200 dark:border-rose-900/30'
  | 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-100 border-emerald-200 dark:border-emerald-900/30'
  | 'bg-sky-50 dark:bg-sky-950/20 text-sky-900 dark:text-sky-100 border-sky-200 dark:border-sky-900/30'
  | 'bg-indigo-50 dark:bg-indigo-950/20 text-indigo-900 dark:text-indigo-100 border-indigo-200 dark:border-indigo-900/30'
  | 'bg-purple-50 dark:bg-purple-950/20 text-purple-900 dark:text-purple-100 border-purple-200 dark:border-purple-900/30'
  | 'bg-zinc-50 dark:bg-zinc-900/40 text-zinc-900 dark:text-zinc-100 border-zinc-200 dark:border-zinc-800'
  | 'bg-orange-50 dark:bg-orange-950/20 text-orange-900 dark:text-orange-100 border-orange-200 dark:border-orange-900/30';

export interface ColorPreset {
  name: string;
  class: NoteColor;
  hex: string;
}

export const NOTE_COLORS: ColorPreset[] = [
  { name: 'Yellow Gold', class: 'bg-amber-50 dark:bg-amber-950/20 text-amber-900 dark:text-amber-100 border-amber-200 dark:border-amber-900/30', hex: '#fef3c7' },
  { name: 'Coral Red', class: 'bg-rose-50 dark:bg-rose-950/20 text-rose-900 dark:text-rose-100 border-rose-200 dark:border-rose-900/30', hex: '#ffe4e6' },
  { name: 'Forest Green', class: 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-100 border-emerald-200 dark:border-emerald-900/30', hex: '#d1fae5' },
  { name: 'Ocean Blue', class: 'bg-sky-50 dark:bg-sky-950/20 text-sky-900 dark:text-sky-100 border-sky-200 dark:border-sky-900/30', hex: '#e0f2fe' },
  { name: 'Royal Indigo', class: 'bg-indigo-50 dark:bg-indigo-950/20 text-indigo-900 dark:text-indigo-100 border-indigo-200 dark:border-indigo-900/30', hex: '#e0e7ff' },
  { name: 'Amethyst', class: 'bg-purple-50 dark:bg-purple-950/20 text-purple-900 dark:text-purple-100 border-purple-200 dark:border-purple-900/30', hex: '#f3e8ff' },
  { name: 'Sunset Orange', class: 'bg-orange-50 dark:bg-orange-950/20 text-orange-900 dark:text-orange-100 border-orange-200 dark:border-orange-900/30', hex: '#ffedd5' },
  { name: 'Classic Slate', class: 'bg-zinc-50 dark:bg-zinc-900/40 text-zinc-900 dark:text-zinc-100 border-zinc-200 dark:border-zinc-800', hex: '#f4f4f5' },
];

export type CardType = 'text' | 'checklist' | 'sketch' | 'audio' | 'photo';

export interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
}

export interface NoteCard {
  id: string;
  notebookId: string;
  type: CardType;
  title: string;
  colorClass: NoteColor;
  createdAt: number;
  updatedAt: number;
  isFavorite: boolean;
  isLocked: boolean;
  pinCode?: string; // Standard 4-digit PIN lock
  tags: string[];
  reminderTime?: number; // timestamp
  isDeleted?: boolean;
  deletedAt?: number;

  // Content bases
  textContent?: string;
  contentHtml?: string;
  checklistContent?: ChecklistItem[];
  sketchDataUrl?: string; // canvas drawings base64
  audioUrl?: string; // audio blob uri or data url
  audioDuration?: number; // duration in seconds
  photoUrl?: string; // photo data url or attachment source
  deskThemeIdx?: number;
  boardX?: number;
  boardY?: number;
  boardWidth?: number;
  boardHeight?: number;
  boardColor?: string;
  isPinned?: boolean;
  boardGroupId?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  textAlign?: string;
  lineHeight?: number;
}

export interface Notebook {
  id: string;
  title: string;
  coverStyle: {
    type: 'gradient' | 'pattern' | 'solid' | 'image';
    primaryColor: string;
    secondaryColor?: string;
    patternName?: string;
    imageTheme?: 'bicycle' | 'retro' | 'nature' | 'starry';
  };
  createdAt: number;
  updatedAt?: number;
  isDeleted?: boolean;
  deletedAt?: number;
  isLocked?: boolean;
  isShared?: boolean;
  pinCode?: string;
}

export type SortOption = 'updated-desc' | 'updated-asc' | 'title-desc' | 'title-asc' | 'created-desc';

export interface DeskTheme {
  name: string;
  bgValue: string;
  textValue: string;
  accentBorder: string;
  buttonHoverBg: string;
  toolbarBg: string;
  metaBadgeBg: string;
}

export const DESK_THEMES: DeskTheme[] = [
  {
    name: 'Sage Green',
    bgValue: '#cbd5c5',
    textValue: '#1f2421',
    accentBorder: '#b6c4b0',
    buttonHoverBg: 'rgba(0,0,0,0.06)',
    toolbarBg: 'rgba(255,255,255,0.85)',
    metaBadgeBg: 'rgba(0,0,0,0.05)'
  },
  {
    name: 'Sand Desk',
    bgValue: '#ecdcc9',
    textValue: '#3b3127',
    accentBorder: '#dcbba2',
    buttonHoverBg: 'rgba(0,0,0,0.06)',
    toolbarBg: 'rgba(255,255,255,0.85)',
    metaBadgeBg: 'rgba(0,0,0,0.05)'
  },
  {
    name: 'Cherry Blossom',
    bgValue: '#fcdbdc',
    textValue: '#451e21',
    accentBorder: '#f0adb2',
    buttonHoverBg: 'rgba(0,0,0,0.06)',
    toolbarBg: 'rgba(255,255,255,0.85)',
    metaBadgeBg: 'rgba(0,0,0,0.05)'
  },
  {
    name: 'Mint Breeze',
    bgValue: '#cce8e1',
    textValue: '#112e27',
    accentBorder: '#a9d9cd',
    buttonHoverBg: 'rgba(0,0,0,0.06)',
    toolbarBg: 'rgba(255,255,255,0.85)',
    metaBadgeBg: 'rgba(0,0,0,0.05)'
  },
  {
    name: 'Peach Sunset',
    bgValue: '#fedec9',
    textValue: '#4a1e04',
    accentBorder: '#ebb998',
    buttonHoverBg: 'rgba(0,0,0,0.06)',
    toolbarBg: 'rgba(255,255,255,0.85)',
    metaBadgeBg: 'rgba(0,0,0,0.05)'
  },
  {
    name: 'Lavender Fields',
    bgValue: '#e7dff2',
    textValue: '#2d1847',
    accentBorder: '#cbbae0',
    buttonHoverBg: 'rgba(0,0,0,0.06)',
    toolbarBg: 'rgba(255,255,255,0.85)',
    metaBadgeBg: 'rgba(0,0,0,0.05)'
  },
  {
    name: 'Studio Slate',
    bgValue: '#f1f3f5',
    textValue: '#212529',
    accentBorder: '#dee2e6',
    buttonHoverBg: 'rgba(0,0,0,0.05)',
    toolbarBg: 'rgba(255,255,255,0.9)',
    metaBadgeBg: 'rgba(0,0,0,0.05)'
  }
];
